import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, UploadCloud, FileText, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const enconOrange = '#ee7203';
const money = (v) => (v === null || v === undefined ? '—' : `£${Number(v).toFixed(2)}`);
const tierName = (t) => `${t.rank === 1 ? 'Best volume' : `Tier ${t.rank}`} — ${t.labels.join(' / ')}`;

/**
 * Update Price List (PDF) — Settings > Pricing > (supplier).
 *
 * 1. Choose a PDF. It is READ by Claude in the background (nothing is saved yet).
 * 2. REVIEW: every product row that was read and verified against its own line of the PDF, the price column(s) it found
 *    (volume tiers / per m² / per board / per pack ...) with a choice of which to use, exactly what would be updated or
 *    added (old price -> new price), any rows that could not be verified, and any lines that look like products but were not
 *    read. The user can change the price column and watch the preview change.
 * 3. APPLY: only now are prices saved (one History entry, so it can be reverted).
 */
export default function PriceListReaderModal({ supplier, open, onClose, onUpdated }) {
  const [stage, setStage] = useState('choose'); // choose | reading | review | applying
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [tierRank, setTierRank] = useState(1);
  const [basis, setBasis] = useState('auto');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [showFlagged, setShowFlagged] = useState(false);
  const [showUnread, setShowUnread] = useState(false);
  const [selectedLengths, setSelectedLengths] = useState([]);
  const [openRows, setOpenRows] = useState({});
  const lengthsInit = useRef(false);
  const pollRef = useRef(null);
  const previewSeq = useRef(0);
  const jobRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => { jobRef.current = job; }, [job]);

  useEffect(() => {
    if (open) {
      setStage('choose'); setFile(null); setJob(null); setPreview(null); setProgress({ done: 0, total: 0 });
      setTierRank(1); setBasis('auto'); setShowFlagged(false); setShowUnread(false);
      setSelectedLengths([]); setOpenRows({}); lengthsInit.current = false;
    }
    return () => stopPolling();
  }, [open]);

  const poll = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`${API}/pricing/pdf-reads/${jobId}`);
        const j = res.data;
        setProgress({ done: j.completed_pages || 0, total: j.total_pages || 0 });
        if (j.status === 'review') {
          stopPolling();
          setJob(j);
          setTierRank(j.default_tier_rank || 1);
          setBasis(j.default_basis || 'auto');
          setStage('review');
        } else if (j.status === 'failed') {
          stopPolling();
          toast.error(j.error || 'Could not read this PDF');
          setStage('choose');
        }
      } catch {
        stopPolling();
        toast.error('Lost connection while reading this PDF — please try again');
        setStage('choose');
      }
    }, 2000);
  };

  const startRead = async () => {
    if (!file) { toast.error('Choose a PDF first'); return; }
    setStage('reading');
    setProgress({ done: 0, total: 0 });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/pricing/suppliers/${supplier.id}/read-pdf`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProgress({ done: 0, total: res.data.total_pages || 0 });
      poll(res.data.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start reading this PDF');
      setStage('choose');
    }
  };

  // Re-work the preview whenever the chosen price column changes.
  useEffect(() => {
    if (stage !== 'review' || !job) return undefined;
    const mine = ++previewSeq.current;
    setPreviewing(true);
    api.post(`${API}/pricing/pdf-reads/${job.id}/preview`, { tier_rank: tierRank, basis })
      .then((r) => {
        if (mine !== previewSeq.current) return;
        setPreview(r.data);
        const proposed = (r.data.new_lengths || []).map((n) => n.stock_code);
        setSelectedLengths((prev) => (lengthsInit.current ? prev.filter((c) => proposed.includes(c)) : proposed));
        lengthsInit.current = true;
      })
      .catch((e) => { if (mine === previewSeq.current) toast.error(e?.response?.data?.detail || 'Could not work out the preview'); })
      .finally(() => { if (mine === previewSeq.current) setPreviewing(false); });
    return undefined;
  }, [stage, job, tierRank, basis]);

  const discard = async () => {
    const j = jobRef.current;
    if (j?.id) { try { await api.delete(`${API}/pricing/pdf-reads/${j.id}`); } catch { /* non-fatal */ } }
  };

  const closeAll = async () => {
    stopPolling();
    if (stage === 'review') await discard();
    onClose();
  };

  const apply = async () => {
    setStage('applying');
    try {
      const body = { tier_rank: tierRank, basis };
      if (preview?.fan_out) body.new_length_codes = selectedLengths;
      const res = await api.post(`${API}/pricing/pdf-reads/${job.id}/apply`, body);
      const added = res.data?.new_lengths || 0;
      toast.success((res.data?.message || 'Price list applied') + (added ? ` · ${added} new stock-code length${added === 1 ? '' : 's'} added` : ''));
      (res.data?.warnings || []).forEach((w) => toast.warning(w, { duration: 15000 }));
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply this price list');
      setStage('review');
    }
  };

  const choices = job?.choices || { tiers: [], bases: [] };
  const counts = preview?.counts;
  const nothingToDo = counts && counts.update + counts.create === 0 && !(preview?.fan_out && selectedLengths.length > 0);
  const toggleRow = (key) => setOpenRows((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleLength = (code) => setSelectedLengths((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && stage !== 'reading' && stage !== 'applying') closeAll(); }}>
      <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="pricing-pdf-reader-dialog">
        <DialogHeader><DialogTitle>Update Price List — {supplier.name}</DialogTitle></DialogHeader>

        {(stage === 'choose' || stage === 'reading') && (
          <>
            <p className="text-xs text-gray-500">
              Upload any {supplier.name} price list or quote PDF — any length or layout. It is read in full and checked line by line.
              Nothing is saved until you have reviewed it and clicked Apply.
            </p>
            <label className="border-2 border-dashed rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-orange-300 transition-colors" data-testid="pricing-pdf-reader-dropzone">
              <input type="file" accept="application/pdf" className="hidden" disabled={stage === 'reading'} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="pricing-pdf-reader-file-input" />
              {file ? (
                <><FileText className="w-6 h-6 text-orange-500" /><span className="text-sm font-medium">{file.name}</span></>
              ) : (
                <><UploadCloud className="w-6 h-6 text-gray-400" /><span className="text-sm text-gray-500">Click to choose a PDF</span></>
              )}
            </label>
            {stage === 'reading' && (
              <p className="text-xs text-gray-500" data-testid="pricing-pdf-reader-progress">
                {progress.total > 0 ? `Reading page ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…` : 'Starting…'}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={closeAll} disabled={stage === 'reading'}>Cancel</Button>
              <Button style={{ backgroundColor: enconOrange }} className="text-white" onClick={startRead} disabled={!file || stage === 'reading'} data-testid="pricing-pdf-reader-read-btn">
                {stage === 'reading' ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Reading…</> : 'Read PDF'}
              </Button>
            </DialogFooter>
          </>
        )}

        {(stage === 'review' || stage === 'applying') && job && (
          <div className="space-y-4" data-testid="pricing-pdf-reader-review">
            <div className="rounded-xl bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-gray-800">
              <span className="font-bold">{job.summary?.rows_read} product rows</span> read from {job.summary?.pages} page{job.summary?.pages === 1 ? '' : 's'} of{' '}
              <span className="font-medium">{job.filename}</span>
              {job.document?.effective_date && <> · prices effective <span className="font-medium">{job.document.effective_date}</span></>}
              <div className="text-xs text-gray-500 mt-1">Every code and price below was checked against the exact line of the PDF it came from.</div>
            </div>

            {(job.notes || []).map((n, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {n}</div>
            ))}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pricing-pdf-reader-columns">
              <div>
                <label className="text-xs font-semibold text-gray-600">Which price should be used?</label>
                <select
                  value={tierRank}
                  onChange={(e) => setTierRank(parseInt(e.target.value, 10))}
                  disabled={stage === 'applying' || choices.tiers.length <= 1}
                  className="mt-1 w-full border-2 border-gray-300 rounded-lg px-2 h-10 text-sm bg-white focus:border-orange-400 focus:outline-none"
                  data-testid="pricing-pdf-reader-tier-select"
                >
                  {choices.tiers.map((t) => <option key={t.rank} value={t.rank}>{tierName(t)}</option>)}
                </select>
                {choices.tiers.length <= 1 && <p className="text-[11px] text-gray-400 mt-1">This PDF has a single price column.</p>}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Price is per</label>
                <select
                  value={basis}
                  onChange={(e) => setBasis(e.target.value)}
                  disabled={stage === 'applying' || choices.bases.length <= 1}
                  className="mt-1 w-full border-2 border-gray-300 rounded-lg px-2 h-10 text-sm bg-white focus:border-orange-400 focus:outline-none"
                  data-testid="pricing-pdf-reader-basis-select"
                >
                  <option value="auto">Automatic (per m² when the PDF has it)</option>
                  {choices.bases.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              </div>
            </div>

            {previewing && !preview ? (
              <div className="flex items-center justify-center py-6 gap-2 text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Working out what will change…</div>
            ) : preview && (
              <>
                <div className={`flex flex-wrap gap-2 text-xs ${previewing ? 'opacity-50' : ''}`} data-testid="pricing-pdf-reader-counts">
                  <span className="rounded-full bg-blue-50 text-blue-700 font-semibold px-3 py-1">{counts.update} to update</span>
                  <span className="rounded-full bg-green-50 text-green-700 font-semibold px-3 py-1">{counts.create} new product{counts.create === 1 ? '' : 's'}</span>
                  {counts.unchanged > 0 && <span className="rounded-full bg-gray-100 text-gray-600 font-semibold px-3 py-1">{counts.unchanged} unchanged</span>}
                  {counts.linked > 0 && <span className="rounded-full bg-purple-50 text-purple-700 font-semibold px-3 py-1">{counts.linked} linked to stock codes</span>}
                  {counts.needs_stock_code > 0 && <span className="rounded-full bg-amber-50 text-amber-700 font-semibold px-3 py-1">{counts.needs_stock_code} new without a stock code</span>}
                  {counts.poa > 0 && <span className="rounded-full bg-gray-100 text-gray-600 font-semibold px-3 py-1">{counts.poa} POA (no price — skipped)</span>}
                  {counts.large_change > 0 && <span className="rounded-full bg-amber-50 text-amber-700 font-semibold px-3 py-1">{counts.large_change} price{counts.large_change === 1 ? '' : 's'} change by more than 25%</span>}
                  {counts.unit_changed > 0 && <span className="rounded-full bg-amber-50 text-amber-700 font-semibold px-3 py-1">{counts.unit_changed} unit change{counts.unit_changed === 1 ? '' : 's'}</span>}
                </div>

                {preview.new_categories.length > 0 && (
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold">New categories that will be created:</span> {preview.new_categories.join(' · ')}
                  </p>
                )}

                <div className="border rounded-xl overflow-hidden">
                  <div className="grid grid-cols-[130px_minmax(0,1fr)_minmax(0,180px)_70px_70px_minmax(0,200px)] gap-2 px-3 py-2 bg-gray-50 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    <div>Code</div><div>Description</div><div>Category</div><div className="text-right">Was</div><div className="text-right">Now</div><div>Notes</div>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y" data-testid="pricing-pdf-reader-sample">
                    {preview.sample.map((r, i) => (
                      <div key={`${r.code}-${i}`} className="grid grid-cols-[130px_minmax(0,1fr)_minmax(0,180px)_70px_70px_minmax(0,200px)] gap-2 px-3 py-1.5 text-xs items-start">
                        <div className="font-mono font-semibold text-gray-800 break-all">{r.code}</div>
                        <div className="text-gray-700">{r.description}</div>
                        <div className="text-gray-500 truncate" title={r.category}>{r.category}</div>
                        <div className="text-right tabular-nums text-gray-500">{r.action === 'create' ? 'new' : money(r.old_price)}</div>
                        <div className="text-right tabular-nums font-bold text-gray-900">{money(r.new_price)}<span className="block text-[10px] font-normal text-gray-400">/{r.uom}</span></div>
                        <div className="space-y-0.5">
                          {r.flags.map((f, k) => <div key={k} className="text-amber-700">{f}</div>)}
                          {r.members && r.members.length > 0 && (
                            <div>
                              <button type="button" onClick={() => toggleRow(`${r.code}-${i}`)} className="inline-flex items-center gap-1 text-blue-700 font-semibold" data-testid="pricing-pdf-reader-expand-row">
                                updates {r.updates_codes} stock code{r.updates_codes === 1 ? '' : 's'}
                                {openRows[`${r.code}-${i}`] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              </button>
                              {openRows[`${r.code}-${i}`] && (
                                <div className="mt-1 max-h-40 overflow-y-auto space-y-0.5 font-mono text-[10px] text-gray-600">
                                  {r.members.map((m, k) => <div key={k}>{m.stock_code || 'no stock code'} {money(m.old_price)} → {money(m.new_price)}</div>)}
                                </div>
                              )}
                            </div>
                          )}
                          {r.stock_code && <div className="text-purple-700">Linked: {r.stock_code}</div>}
                          {!r.stock_code && r.stock_candidates && r.stock_candidates.length > 0 && (
                            preview.fan_out
                              ? <div className="text-gray-500">Possible 53M codes (not linked): {r.stock_candidates.map((c) => c.code).join(', ')}</div>
                              : <div className="text-gray-400">{r.stock_candidates.length} possible stock codes — pick one later</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-gray-400">Showing the rows that need attention first (up to 80). Everything else is applied the same way.</p>

                {preview.fan_out && (preview.inactive_categories || []).length > 0 && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="pricing-pdf-reader-inactive-cats">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>
                      These categories are switched off, so the quote price lookup finds nothing for their products:{' '}
                      <span className="font-semibold">{preview.inactive_categories.join(' · ')}</span>. Switch them on in Settings &gt; Pricing before quoting.
                    </span>
                  </div>
                )}

                {preview.fan_out && (preview.new_lengths || []).length > 0 && (
                  <div className="border border-green-200 rounded-xl" data-testid="pricing-pdf-reader-new-lengths">
                    <div className="px-3 py-2 text-sm font-semibold text-green-800">
                      {preview.new_lengths.length} new stock-code length{preview.new_lengths.length === 1 ? '' : 's'} found for existing groups — ticked ones are added
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y text-xs">
                      {preview.new_lengths.map((n) => (
                        <label key={n.stock_code} className="flex items-start gap-2 px-3 py-1.5 cursor-pointer">
                          <input type="checkbox" checked={selectedLengths.includes(n.stock_code)} onChange={() => toggleLength(n.stock_code)} disabled={stage === 'applying'} data-testid="pricing-pdf-reader-length-checkbox" />
                          <span className="font-mono font-semibold text-gray-800">{n.stock_code}</span>
                          <span className="flex-1 text-gray-700">{n.description}</span>
                          <span className="text-gray-500">joins {n.group_code} · {n.category_name}</span>
                          <span className="tabular-nums font-bold text-gray-900">{money(n.new_price)}/{n.uom}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {preview.fan_out && (preview.unassigned_codes || []).length > 0 && (
                  <div className="border border-amber-200 rounded-xl" data-testid="pricing-pdf-reader-unassigned">
                    <div className="px-3 py-2 text-sm font-semibold text-amber-800">
                      {preview.unassigned_codes.length} unassigned 53M code{preview.unassigned_codes.length === 1 ? '' : 's'} — not added, place {preview.unassigned_codes.length === 1 ? 'it' : 'them'} by hand
                    </div>
                    <div className="max-h-40 overflow-y-auto divide-y text-xs">
                      {preview.unassigned_codes.map((u) => (
                        <div key={u.code} className="px-3 py-1.5">
                          <span className="font-mono font-semibold text-gray-800">{u.code}</span> <span className="text-gray-700">{u.name}</span> <span className="text-gray-400">— {u.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {(job.flagged || []).length > 0 && (
              <div className="border border-amber-200 rounded-xl">
                <button type="button" onClick={() => setShowFlagged((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-amber-800" data-testid="pricing-pdf-reader-flagged-toggle">
                  <AlertTriangle className="w-4 h-4" /> {job.summary.flagged} row{job.summary.flagged === 1 ? '' : 's'} could not be verified and will NOT be imported
                  <span className="ml-auto">{showFlagged ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                </button>
                {showFlagged && (
                  <div className="max-h-48 overflow-y-auto divide-y text-xs">
                    {job.flagged.map((f, i) => (
                      <div key={i} className="px-3 py-1.5"><span className="text-gray-400">p{f.page}{f.line !== null ? ` · line ${f.line}` : ''}</span> <span className="text-amber-800">{f.reason}</span><div className="font-mono text-gray-600 break-all">{f.text}</div></div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(job.unread_lines || []).length > 0 && (
              <div className="border border-amber-200 rounded-xl">
                <button type="button" onClick={() => setShowUnread((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-amber-800" data-testid="pricing-pdf-reader-unread-toggle">
                  <AlertTriangle className="w-4 h-4" /> {job.summary.unread} line{job.summary.unread === 1 ? '' : 's'} look like products but were not read
                  <span className="ml-auto">{showUnread ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                </button>
                {showUnread && (
                  <div className="max-h-48 overflow-y-auto divide-y text-xs">
                    {job.unread_lines.map((u, i) => (
                      <div key={i} className="px-3 py-1.5"><span className="text-gray-400">p{u.page} · line {u.line}</span><div className="font-mono text-gray-600 break-all">{u.text}</div></div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeAll} disabled={stage === 'applying'} data-testid="pricing-pdf-reader-discard-btn">Discard</Button>
              <Button
                style={{ backgroundColor: enconOrange }}
                className="text-white"
                onClick={apply}
                disabled={stage === 'applying' || previewing || !preview || nothingToDo}
                data-testid="pricing-pdf-reader-apply-btn"
              >
                {stage === 'applying' ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Applying…</> : 'Apply Prices'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
